/**
 * Template API client functions — called via TanStack Query.
 * All requests carry the Clerk session token via the shared api-client.
 */

import { useCallback } from "react";

import { useAppAuth } from "@/lib/auth";

import type {
  TemplateCreate,
  TemplateOut,
  TemplateUpdate,
} from "./types";

const BASE = "/api/v1/templates";

function useAuthHeaders() {
  const { getToken } = useAppAuth();
  return useCallback(async () => {
    const token = await getToken();
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [getToken]);
}

export function useTemplateApi() {
  const getHeaders = useAuthHeaders();

  async function listTemplates(params?: {
    type?: string;
    status?: string;
  }): Promise<TemplateOut[]> {
    const headers = await getHeaders();
    const qs = new URLSearchParams();
    if (params?.type) qs.set("type", params.type);
    if (params?.status) qs.set("status", params.status);
    const url = qs.size ? `${BASE}?${qs}` : BASE;
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<TemplateOut[]>;
  }

  async function getTemplate(id: string): Promise<TemplateOut> {
    const headers = await getHeaders();
    const res = await fetch(`${BASE}/${id}`, { headers });
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<TemplateOut>;
  }

  async function createTemplate(body: TemplateCreate): Promise<TemplateOut> {
    const headers = await getHeaders();
    const res = await fetch(BASE, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<TemplateOut>;
  }

  async function updateTemplate(
    id: string,
    body: TemplateUpdate,
  ): Promise<TemplateOut> {
    const headers = await getHeaders();
    const res = await fetch(`${BASE}/${id}`, {
      method: "PATCH",
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<TemplateOut>;
  }

  async function deleteTemplate(id: string): Promise<void> {
    const headers = await getHeaders();
    const res = await fetch(`${BASE}/${id}`, { method: "DELETE", headers });
    if (!res.ok) throw new Error(await res.text());
  }

  async function activateTemplate(id: string): Promise<TemplateOut> {
    const headers = await getHeaders();
    const res = await fetch(`${BASE}/${id}/activate`, {
      method: "POST",
      headers,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<TemplateOut>;
  }

  async function deactivateTemplate(id: string): Promise<TemplateOut> {
    const headers = await getHeaders();
    const res = await fetch(`${BASE}/${id}/deactivate`, {
      method: "POST",
      headers,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<TemplateOut>;
  }

  async function duplicateTemplate(id: string): Promise<TemplateOut> {
    const headers = await getHeaders();
    const res = await fetch(`${BASE}/${id}/duplicate`, {
      method: "POST",
      headers,
    });
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<TemplateOut>;
  }

  return {
    listTemplates,
    getTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    activateTemplate,
    deactivateTemplate,
    duplicateTemplate,
  };
}
